import { ParamMap, Params } from '@angular/router';
import {
  DsmCatalogChannel,
  DsmCatalogFilterRoom,
  DsmCatalogFilters,
  DsmCatalogFilterValue,
  DsmCatalogOptionScope,
  DsmCatalogQuery
} from '../models/dsm-options-catalog.models';
import { fromDsmModelElevationKey, toDsmModelElevationKey } from './dsm-model-elevation-key.util';

/** The Options Catalog page's query param names — the URL is the page's only state, so a view can be shared and reloaded. */
export enum DsmCatalogParam {
  ProjectId = 'projectId',
  /** the chosen plans, as `modelId:elevationId` */
  ModelElevations = 'modelElevations',
  /** an older link's one plan; read once, then replaced by ModelElevations */
  ModelId = 'modelId',
  ElevationId = 'elevationId',
  CategoryIds = 'categoryIds',
  SubcategoryIds = 'subcategoryIds',
  RoomIds = 'roomIds',
  ProductTypeIds = 'productTypeIds',
  Scopes = 'scopes',
  Channels = 'channels',
  HiddenPrices = 'hiddenPrices',
  Details = 'details'
}

export enum DsmCatalogPrice {
  Unit = 'unit',
  Extended = 'extended'
}

/** Extra per-option details, all off by default so the list stays compact. */
export enum DsmCatalogDetail {
  Rooms = 'rooms',
  Attributes = 'attributes',
  Image = 'image'
}

export interface DsmCatalogUrlState {
  projectId: string | null;
  /** the chosen home plans, as `modelId:elevationId`, in the order picked */
  modelElevations: string[];
  categoryIds: string[];
  subcategoryIds: string[];
  roomIds: string[];
  productTypeIds: string[];
  scopes: DsmCatalogOptionScope[];
  channels: DsmCatalogChannel[];
  /** Prices are shown unless hidden, so an empty URL shows them all and hiding both survives a reload. */
  hiddenPrices: DsmCatalogPrice[];
  details: DsmCatalogDetail[];
}

export type DsmCatalogFilterState = Pick<
  DsmCatalogUrlState,
  'categoryIds' | 'subcategoryIds' | 'roomIds' | 'productTypeIds' | 'scopes' | 'channels'
>;

export const DSM_CATALOG_NO_FILTERS: DsmCatalogFilterState = {
  categoryIds: [],
  subcategoryIds: [],
  roomIds: [],
  productTypeIds: [],
  scopes: [],
  channels: []
};

export function readCatalogUrlState(params: ParamMap): DsmCatalogUrlState {
  const known = <T extends string>(key: DsmCatalogParam, values: Record<string, T>): T[] =>
    params.getAll(key).filter((value): value is T => Object.values<string>(values).includes(value));

  const plans = params.getAll(DsmCatalogParam.ModelElevations).filter(key => fromDsmModelElevationKey(key).modelId);
  const olderPlan = toDsmModelElevationKey(
    params.get(DsmCatalogParam.ModelId),
    params.get(DsmCatalogParam.ElevationId)
  );

  return {
    projectId: params.get(DsmCatalogParam.ProjectId),
    modelElevations: plans.length || !olderPlan ? plans : [olderPlan],
    categoryIds: params.getAll(DsmCatalogParam.CategoryIds),
    subcategoryIds: params.getAll(DsmCatalogParam.SubcategoryIds),
    roomIds: params.getAll(DsmCatalogParam.RoomIds),
    productTypeIds: params.getAll(DsmCatalogParam.ProductTypeIds),
    scopes: known(DsmCatalogParam.Scopes, DsmCatalogOptionScope).filter(scope => scope !== DsmCatalogOptionScope.All),
    channels: known(DsmCatalogParam.Channels, DsmCatalogChannel),
    hiddenPrices: known(DsmCatalogParam.HiddenPrices, DsmCatalogPrice),
    details: known(DsmCatalogParam.Details, DsmCatalogDetail)
  };
}

/**
 * For `router.navigate` with `queryParamsHandling: 'merge'`: an emptied list or cleared id is removed from
 * the URL, and writing the plans removes an older link's single model/elevation.
 */
export function toCatalogQueryParams(patch: Partial<DsmCatalogUrlState>): Params {
  const params: Params = Object.fromEntries(
    Object.entries(patch).map(([key, value]) => [key, Array.isArray(value) && !value.length ? null : value])
  );
  return 'modelElevations' in patch
    ? { ...params, [DsmCatalogParam.ModelId]: null, [DsmCatalogParam.ElevationId]: null }
    : params;
}

/** A new project: its plans and filters belong to the old one, so they are cleared. */
export function toProjectPatch(projectId: string | null): Partial<DsmCatalogUrlState> {
  return { projectId, modelElevations: [], ...DSM_CATALOG_NO_FILTERS };
}

/**
 * Adding or removing a plan keeps the filters: whatever the new set no longer offers is dropped by
 * `sanitizeCatalogState` once its lookups load.
 */
export function toModelElevationsPatch(modelElevations: string[]): Partial<DsmCatalogUrlState> {
  return { modelElevations };
}

/** Subcategories to offer: hidden ones never, and only the chosen categories' once any is chosen. */
export function visibleSubcategories(
  subcategories: DsmCatalogFilterValue[],
  categoryIds: string[]
): DsmCatalogFilterValue[] {
  return subcategories.filter(
    ({ isHidden, categoryId }) => !isHidden && (!categoryIds.length || categoryIds.includes(categoryId ?? ''))
  );
}

/**
 * The room lookup holds several rooms with the same name (separate locations with different codes).
 * Shown once per name; picking a name filters by every id that carries it.
 */
export function groupRoomsByName(rooms: DsmCatalogFilterRoom[]): { name: string; ids: string[] }[] {
  const byName = new Map<string, string[]>();
  rooms.forEach(({ id, name }) => byName.set(name, [...(byName.get(name) ?? []), id]));

  return [...byName].map(([name, ids]) => ({ name, ids }));
}

/**
 * Keeps only the ids this home plan offers. A shared or old link can carry a category that is now
 * hidden, a room of another plan, a retired product type or a subcategory of an unchosen category;
 * sent as is, they would
 * silently empty the list.
 */
export function sanitizeCatalogState(
  state: DsmCatalogUrlState,
  lookups: DsmCatalogFilters['filters'],
  productTypes: { id: string }[] | null
): DsmCatalogUrlState {
  const ids = (values: { id: string }[]) => new Set(values.map(({ id }) => id));

  const shownCategories = ids(lookups.categories.filter(({ isHidden }) => !isHidden));
  const categoryIds = state.categoryIds.filter(id => shownCategories.has(id));
  const offeredSubcategories = ids(visibleSubcategories(lookups.subcategories, categoryIds));
  const rooms = ids(lookups.rooms);

  return {
    ...state,
    categoryIds,
    subcategoryIds: state.subcategoryIds.filter(id => offeredSubcategories.has(id)),
    roomIds: state.roomIds.filter(id => rooms.has(id)),
    // product types that could not be loaded are left as chosen, rather than silently dropped
    productTypeIds: productTypes ? state.productTypeIds.filter(id => ids(productTypes).has(id)) : state.productTypeIds
  };
}

/**
 * The API query for the page, or null until a project and at least one plan are chosen; the first plan is
 * the API's context. Prices are display only,
 * so they never change the query. No channel chosen means both, so Sales Office options are not
 * hidden behind the API's Design Center default.
 */
export function toCatalogQuery(state: DsmCatalogUrlState): DsmCatalogQuery | null {
  const { projectId, modelElevations } = state;
  const { modelId, elevationId } = fromDsmModelElevationKey(modelElevations[0]);
  if (!projectId || !modelId || !elevationId) return null;

  return {
    projectId,
    modelId,
    elevationId,
    modelElevations,
    categoryIds: state.categoryIds,
    subcategoryIds: state.subcategoryIds,
    roomIds: state.roomIds,
    productTypeIds: state.productTypeIds,
    optionScope: state.scopes.length === 1 ? state.scopes[0] : DsmCatalogOptionScope.All,
    catalogChannels: state.channels.length
      ? state.channels
      : [DsmCatalogChannel.SalesOffice, DsmCatalogChannel.DesignCenter],
    includeRooms: state.details.includes(DsmCatalogDetail.Rooms),
    includeAttributes: state.details.includes(DsmCatalogDetail.Attributes),
    includeImages: state.details.includes(DsmCatalogDetail.Image)
  };
}
